import { useSEO } from "../hooks/useSEO";
import { PolicyShell, H, BUSINESS } from "./_policyShell";

const Refund = () => {
  useSEO({
    title: "Refund & Cancellation Policy",
    description:
      "Sterling Bio Ltd refund and cancellation policy. 14-day UK Consumer Contracts cooling-off period, 120-day card scheme refund window, full procedure for damaged or incorrect orders.",
    path: "/refund-policy",
  });
  return (
    <PolicyShell
      kicker="Legal"
      title="Refund & Cancellation Policy"
      testid="refund-page"
    >
      <p className="text-sm text-[#64748B] font-mono uppercase tracking-wider">
        Last updated: 25 February 2026
      </p>
      <p>
        This policy explains how to cancel an order placed with{" "}
        <strong>{BUSINESS.name}</strong> (Company No. {BUSINESS.companyNo}) and how refunds are
        processed. It does not affect your statutory rights under UK consumer law.
      </p>

      <H>1. 14-day right to cancel (Consumer Contracts Regulations 2013)</H>
      <p>
        Where you purchase as a consumer (i.e. not for a business or institution), you have the
        right to cancel your contract within <strong>14 days</strong> of the day you (or a
        person nominated by you) receive the goods, without giving any reason. Goods must be
        returned to us within 14 days of you informing us of the cancellation.
      </p>
      <p>
        To exercise this right, email{" "}
        <a className="text-[#2C8C9C] hover:underline" href={`mailto:${BUSINESS.email}`}>
          {BUSINESS.email}
        </a>{" "}
        or call{" "}
        <a className="text-[#2C8C9C] hover:underline" href={`tel:${BUSINESS.phone}`}>
          {BUSINESS.phoneDisplay}
        </a>{" "}
        quoting your order number. Important: the right to cancel is reduced for goods that, by
        their nature, become unsuitable for return once unsealed (e.g. sealed laboratory
        consumables) — see §3 below.
      </p>

      <H>2. Eligibility for refund</H>
      <p>You may request a full refund within 14 days of receiving your order if:</p>
      <ul className="list-disc list-outside pl-6 space-y-2">
        <li>The product was damaged during transit and you provide photographic evidence within 48 hours.</li>
        <li>The product received does not match what was ordered.</li>
        <li>An order is cancelled prior to dispatch (full refund, no restocking fee).</li>
        <li>You exercise your 14-day Consumer Contracts cancellation right and the seal is intact.</li>
      </ul>

      <H>3. Non-refundable items</H>
      <ul className="list-disc list-outside pl-6 space-y-2">
        <li>Products where the tamper-evident seal has been broken, opened or reconstituted (excluded from cancellation rights under reg. 28(3) Consumer Contracts Regulations 2013).</li>
        <li>Products beyond the 14-day notification window.</li>
        <li>Custom or special-order items not listed in our standard catalogue.</li>
      </ul>

      <H>4. How to request a refund</H>
      <ol className="list-decimal list-outside pl-6 space-y-2">
        <li>
          Email <a className="text-[#2C8C9C] hover:underline" href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a> or call{" "}
          <a className="text-[#2C8C9C] hover:underline" href={`tel:${BUSINESS.phone}`}>{BUSINESS.phoneDisplay}</a> with your order number, the issue and any supporting photos.
        </li>
        <li>
          We acknowledge within 1 UK business day and provide a return address (where goods need to be returned).
        </li>
        <li>
          Once we receive the goods (or confirm cancellation pre-dispatch), refunds are issued within{" "}
          <strong>7 business days</strong> to the original payment method.
        </li>
      </ol>

      <H>5. Refund method &amp; timing</H>
      <p>
        All payments are taken by <strong>UK bank transfer via Pay-by-Bank</strong>. Refunds are
        returned as a UK Faster Payments transfer to the <strong>originating bank account</strong>{" "}
        (the account that made the original payment). Approved refunds are actioned within{" "}
        <strong>7 business days</strong> of approval; Faster Payments typically settle within
        seconds, some banks confirm in up to 2 hours.
      </p>
      <p>
        We do not currently accept card payments, so no card-scheme chargeback window applies.
      </p>

      <H>6. Return shipping costs</H>
      <p>
        Where the return is due to our error (damaged in transit, wrong item, defective), we
        cover all return shipping costs and reimburse any reasonable postage you incur. Where you
        return goods under your 14-day right to cancel and no fault exists on our side, you are
        responsible for the cost of returning the items.
      </p>

      <H>7. Statutory rights</H>
      <p>
        This policy does not affect your statutory rights under UK law including the Consumer
        Rights Act 2015 and the Consumer Contracts (Information, Cancellation and Additional
        Charges) Regulations 2013. If you have any questions, our team is reachable by phone or
        email during UK business hours (09:00–17:00 GMT, Mon–Fri).
      </p>
    </PolicyShell>
  );
};

export default Refund;
